import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import {
  emitAppEvent,
  subscribeAppEvents,
} from "./events.js";

function Portal({ children }) {
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    children,
    document.body
  );
}

const getOnboardingUserId = () => {
  if (typeof window === "undefined") {
    return 9999;
  }

  try {
    return (
      window.Telegram?.WebApp
        ?.initDataUnsafe?.user?.id ||
      9999
    );
  } catch {
    return 9999;
  }
};

const ONBOARDING_STORAGE_KEY = () =>
  `fitmeup:${getOnboardingUserId()}:onboarding:v1`;

function useFitmeupOnboardingNavVisible(
  active
) {
  useEffect(() => {
    if (
      !active ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const root =
      document.documentElement;

    const count = Number(
      root.dataset
        .fitmeupOnboardingNavCount || 0
    );

    root.dataset
      .fitmeupOnboardingNavCount =
      String(count + 1);

    root.dataset
      .fitmeupOnboardingNav = "1";

    return () => {
      const next = Math.max(
        0,
        Number(
          root.dataset
            .fitmeupOnboardingNavCount || 1
        ) - 1
      );

      if (next > 0) {
        root.dataset
          .fitmeupOnboardingNavCount =
          String(next);

        root.dataset
          .fitmeupOnboardingNav = "1";
      } else {
        delete root.dataset
          .fitmeupOnboardingNavCount;

        delete root.dataset
          .fitmeupOnboardingNav;
      }
    };
  }, [active]);
}
const AUTO_CONTINUE_FOOD_TO_DISH = false;

const ONBOARDING_SCENARIOS = {
overview: {
  id: "overview",
  group: "basic",

  title: "Еда",

description:
  "Рацион, КБЖУ и добавление еды.",

duration: "1 минута",
  previewOnly: false,

    steps: [
      {
  id: "overview-macro-edit",
  kind: "action",
  target:
    '[data-onboarding="macro-edit"]',

  shape: "circle",
  padding: -4,
  placement: "below",

  title: "Настройте свой КБЖУ",

  actionHint: "Нажмите на карандаш",

  waitFor: "macro-modal-opened",
},

{
  id: "overview-macro-form",
  kind: "action",

  // Это обычная полноэкранная форма:
  // нижнюю навигацию не показываем.
  hideAppNav: true,

  hideTooltip: true,
  hideFinish: true,

  waitFor: "macros-saved",
},






{
  id: "overview-manual-add",
  kind: "info",

  target:
    '[data-onboarding="breakfast-add"]',

  padding: 4,
  placement: "above",

  title:
    "Добавьте еду удобным способом",

  text:
    "- Сфотографируйте блюдо или этикетку продукта\n- Добавляйте руками БЖУ с упаковки",
},









      // Повторной подсветки фото здесь больше нет.

{
  id: "overview-rings",
  kind: "info",

  target:
    '[data-onboarding="macro-rings"]',

  placement: "below",

  title: "Кольца КБЖУ",

text:
  "Кольца показывают ваш дневной прогресс по съеденным калориям.",
},

{
  id: "overview-complete",
  kind: "complete",

  reminderChoice: true,

  title:
    "Всё готово 🚀",

  text:
  "Ваш КБЖУ настроен, и вы уже знаете, как добавлять еду.\n\nКогда наступит следующий реальный приём пищи, откройте FitMeUp и сфотографируйте блюдо или добавьте еду вручную.",
  reminderTitle:
    "Когда вам напомнить?",
},




    ],
  },

workout: {
  id: "workout",
  group: "basic",

  title: "Тренировки",

  description:
    "Первая сессия, сохранение и повтор тренировки.",

  duration: "7 минут",

  steps: [
    {
      id:
        "workout-form-tools",

      kind: "info",

      target:
        '[data-onboarding="workout-form-tools"]',

      centerTarget: true,

      padding: 4,
      placement: "below",

      title:
        "Замеры, шаги и добавки",

      text:
        "В разделе «Форма» можно вести замеры тела, отмечать шаги и контролировать приём добавок.",
    },

    {
      id:
        "workout-muscle-select",

      kind: "action",

      target:
        '[data-onboarding="workout-muscle-grid"]',

      centerTarget: true,

      padding: 4,
      placement: "top-fixed",

      title:
        "Выберите группу мышц",

      actionHint:
        "Нажмите на любую группу",

      waitFor:
        "workout:muscle-selected",
    },

    {
      id:
        "workout-muscle-continue",

      kind: "action",

      target:
        '[data-onboarding="workout-muscle-continue"]',

      padding: 4,
      placement: "above",

      title:
        "Начните тренировку",

      actionHint:
        "Нажмите «Продолжить»",

      waitFor:
        "workout:session-opened",
    },

    {
      id:
        "workout-session-overview",

      kind: "info",

      target:
        '[data-onboarding="workout-session-header"]',

      autoScroll: false,

      padding: 4,
      placement: "below",

      title:
        "Сессия тренировки",

      text:
        "Вверху находятся название тренировки, длительность и общий вес. Ниже — упражнения и подходы.",
    },

    {
      id:
        "workout-exercise-controls",

      kind: "info",

      target:
        '[data-onboarding="workout-first-exercise"]',

      secondaryTarget:
        '[data-onboarding="workout-first-set"]',

      centerTarget: true,

      padding: 4,
      placement: "top-fixed",

      title:
        "Упражнение и подходы",

      text:
        "Нажмите на название упражнения, чтобы изменить его. Нажмите на подход, чтобы указать вес и повторы. Плюс добавляет подход, а крестики удаляют подход или упражнение.",
    },

    {
      id:
        "workout-exercise-drag",

      kind: "info",

      target:
        '[data-onboarding="workout-first-exercise-drag"]',

      shape: "circle",
      padding: 5,
      placement: "below",

      title:
        "Меняйте порядок упражнений",

      text:
        "Зажмите шесть точек и перетащите упражнение на нужное место.",
    },

    {
      id:
        "workout-title-open",

      kind: "action",

      target:
        '[data-onboarding="workout-session-title"]',

      padding: 4,
      placement: "below",

      title:
        "Назовите тренировку",

      actionHint:
        "Нажмите на название",

      waitFor:
        "workout:title-modal-opened",
    },

    {
      id:
        "workout-title-form",

      kind: "action",

      target:
        '[data-onboarding="workout-title-modal"]',

      hideTooltip: true,
      hideAppNav: true,
      hideFinish: true,

      waitFor:
        "workout:title-saved",
    },

    {
      id:
        "workout-save",

      kind: "action",

      target:
        '[data-onboarding="workout-session-save"]',

      padding: 4,
      placement: "below",

      title:
        "Сохраните тренировку",

      actionHint:
        "Нажмите «Сохранить»",

      waitFor:
        "workout:saved",
    },

    {
      id:
        "workout-library-filters",

      kind: "info",

      target:
        '[data-onboarding="workout-library-filters"]',

      padding: 4,
      placement: "below",

      title:
        "Мои тренировки",

      text:
        "Сохранённые тренировки группируются по названию. Фильтры помогают быстро найти нужную программу.",
    },

    {
      id:
        "workout-card-open",

      kind: "action",

      target:
        '[data-onboarding-workout="1"] [data-onboarding="workout-card-expand"]',

      shape: "circle",
      padding: 4,
      placement: "below",

      title:
        "Откройте тренировку",

      actionHint:
        "Нажмите на стрелку",

      waitFor:
        "workout:saved-card-opened",
    },

    {
      id:
        "workout-card-details",

      kind: "info",

      target:
        '[data-onboarding-workout="1"]',

      autoScroll: false,

      padding: 4,
      placement: "above",

      title:
        "История тренировки",

      text:
        "В раскрытой карточке хранятся упражнения, подходы, вес, повторы, длительность и общий объём работы.",
    },

    {
      id:
        "workout-repeat",

      kind: "action",

      target:
        '[data-onboarding-workout="1"] [data-onboarding="workout-repeat"]',

      padding: 4,
      placement: "above",

      title:
        "Повторите тренировку",

      actionHint:
        "Нажмите «Повторить тренировку»",

      waitFor:
        "workout:repeated",
    },

    {
      id:
        "workout-repeat-overview",

      kind: "info",

      target:
        '[data-onboarding="workout-first-exercise"]',

      secondaryTarget:
        '[data-onboarding="workout-first-set"]',

      centerTarget: true,

      padding: 4,
      placement: "top-fixed",

      title:
        "Легко отслеживать прогресс",

      text:
        "Название, упражнения, подходы, прошлые веса и повторы уже перенеслись. Изменяйте значения в новой сессии и сравнивайте результат.",
    },

    {
      id:
        "workout-cancel-repeat",

      kind: "action",

      target:
        '[data-onboarding="workout-cancel-session"]',

      padding: 4,
      placement: "above",

      title:
        "Отменим повтор",

      actionHint:
        "Нажмите «Отменить тренировку» и подтвердите",

      waitFor:
        "workout:cancelled",
    },

    {
      id:
        "workout-activity-calendar",

      kind: "info",

      target:
        '[data-onboarding="workout-activity-calendar"]',

      centerTarget: true,

      padding: 4,
      placement: "above",

      title:
        "Календарь активности",

      text:
        "После сохранённой тренировки в календаре появляется огонёк. Так формируется наглядная история вашей активности.",
    },

    {
      id:
        "workout-complete",

      kind: "complete",

      title:
        "Тренировки освоены!",

      text:
        "Вы создали, сохранили и повторили тренировку и увидели её в календаре активности.",

      buttonLabel:
        "Посмотреть другие онбординги",
    },
  ],
},

dish: {
  id: "dish",
  group: "advanced",

  title:
    "Мои продукты и блюда",

  description:
    "Создание блюда и управление его составом.",

  duration: "4 минуты",

  requires: "overview",

  steps: [
{
  id: "dish-entry",
  kind: "action",

  target:
    '[data-onboarding="my-foods-entry"]',

  padding: 4,
  placement: "below",

  title:
    "Раздел «Мои продукты и блюда»",

  actionHint:
    "Нажмите «Мои блюда»",

  waitFor:
    "my-foods:entered",
},
    {
      id:
        "dish-library-overview",

      kind: "info",

      target:
        '[data-onboarding="my-foods-tabs"]',

      placement: "below",

      title:
        "Продукты и блюда",

      text:
        "В разделе «Продукты» хранятся отдельные ингредиенты с их БЖУ. В разделе «Блюда» — рецепты, собранные из нескольких продуктов.",
    },

{
  id:
    "dish-product-menu",

  kind: "info",

  target:
    '[data-onboarding="my-foods-product-menu"]',

  shape: "circle",
  padding: 4,
  placement: "below",

  title:
    "Редактирование продукта",
},

{
  id:
    "dish-create-intro",

  kind: "info",

  target:
    '[data-onboarding="my-foods-add-button"]',

  placement: "below",

  title:
    "Создайте новое блюдо",
},

{
  id:
    "dish-builder-overview",

  kind: "action",

  target:
    '[data-onboarding="dish-builder-ingredient-list"]',

  autoScroll: false,

  padding: 4,

  hideAppNav: true,
  hideFinish: true,

  placement: "top-fixed",

  title:
    "Добавьте продукт в блюдо",

  actionHint:
    "Нажмите плюс у любого продукта",

  waitFor:
    "dish-builder:ingredient-added",
},

{
  id:
    "dish-builder-save",

  kind: "action",

  target:
    '[data-onboarding="dish-builder"]',

  autoScroll: false,

  hideAppNav: true,
  hideFinish: true,

  placement: "top-fixed",

  overlayOpacity: 0.86,
  dimTarget: true,

  title:
    "Блюдо готово к сохранению",

  actionHint:
    "Нажмите кнопку «Сохранить»",

  waitFor: "dish:created",
},



{
  id:
    "dish-composition-open",

  kind: "action",

  target:
    '[data-onboarding-created-dish="1"] [data-onboarding="my-foods-dish-composition-button"]',

  autoScroll: false,

  shape: "circle",
  padding: 4,
  placement: "below",

  title:
    "Откройте состав блюда",

  actionHint:
    "Нажмите «Состав»",

  waitFor:
    "dish-composition:opened",
},

{
  id:
    "dish-composition-add",

  kind: "info",

  // Основная цель — вся карточка.
  // Поэтому плашка будет размещаться
  // под нижней границей рамки.
  target:
    '[data-onboarding-created-dish="1"]',

  // Кнопку дополнительно подсвечиваем.
  secondaryTarget:
    '[data-onboarding-created-dish="1"] [data-onboarding="my-foods-dish-add-ingredient"]',

  autoScroll: false,

placement: "above",
forcePlacement: true,

  padding: 4,

  title:
    "Добавление ингредиентов",

  text:
    "Здесь вы можете добавить в уже созданное блюдо новые продукты.",
},

{
  id: "dish-complete",
  kind: "complete",

  title:
    "Поздравляем! Вы завершили онбординг по еде",

  text:
    "Теперь вы умеете работать с рационом, создавать продукты и блюда и управлять их составом.",

buttonLabel:
  "Вернуться к рациону",
},
  ],
},
};

function getEmptyOnboardingProgress() {
  return {
    version: 1,

    completed: {
      overview: false,
      workout: false,
      dish: false,
    },
  };
}

function readOnboardingProgress() {
  if (typeof window === "undefined") {
    return getEmptyOnboardingProgress();
  }

  try {
    const raw =
      window.localStorage.getItem(
        ONBOARDING_STORAGE_KEY()
      );

    if (!raw) {
      return getEmptyOnboardingProgress();
    }

    const parsed = JSON.parse(raw);

    return {
      ...getEmptyOnboardingProgress(),
      ...parsed,

      completed: {
        ...getEmptyOnboardingProgress()
          .completed,

        ...(parsed?.completed || {}),
      },
    };
  } catch (error) {
    console.warn(
      "[onboarding] progress read failed:",
      error
    );

    return getEmptyOnboardingProgress();
  }
}

function writeOnboardingProgress(progress) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      ONBOARDING_STORAGE_KEY(),
      JSON.stringify(progress)
    );
  } catch (error) {
    console.warn("[onboarding] progress save failed:", error);
  }
}

// Эту функцию будем вызывать из существующих обработчиков.
// Например после успешного сохранения продукта:
//
// emitOnboardingAction("product-created-and-added");
//
function emitOnboardingAction(
  action,
  payload = {}
) {
  // Временный совместимый экспорт.
  // Новые подключения используют
  // универсальный emitAppEvent.
  emitAppEvent(
    action,
    payload
  );
}

function OnboardingCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none">
      <path
        d="m6.5 12.5 3.3 3.3 7.8-8"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OnboardingLockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none">
      <rect
        x="5"
        y="10"
        width="14"
        height="10"
        rx="3"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M8.5 10V7.8A3.5 3.5 0 0 1 12 4.3a3.5 3.5 0 0 1 3.5 3.5V10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function OnboardingScenarioCard({
  scenario,
  completed,
  locked,
  onClick,
}) {
  return (
    <button
      type="button"
      disabled={locked}
      onClick={onClick}
      className="relative w-full text-left transition active:scale-[0.985] disabled:cursor-not-allowed"
      style={{
        minHeight: 96,
        padding: "15px 16px",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 22,
        background: locked
          ? "rgba(7,17,29,0.72)"
          : "linear-gradient(145deg, rgba(11,36,48,0.92), rgba(4,16,29,0.96))",
        boxShadow: locked
          ? "none"
          : "0 14px 34px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.045)",
        opacity: locked ? 0.48 : 1,
        overflow: "hidden",
      }}
    >
      {!locked ? (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: -45,
            top: -70,
            width: 150,
            height: 150,
            borderRadius: 999,
            background: "rgba(53,243,223,0.075)",
            filter: "blur(38px)",
            pointerEvents: "none",
          }}
        />
      ) : null}

      <span
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          gap: 13,
        }}
      >
        <span
          style={{
            width: 38,
            height: 38,
            flex: "0 0 auto",
            display: "grid",
            placeItems: "center",
            borderRadius: 999,
            background: locked
              ? "rgba(148,163,184,0.08)"
              : completed
                ? "#35F3DF"
                : "rgba(53,243,223,0.11)",
            color: completed
              ? "#031018"
              : locked
                ? "rgba(148,163,184,0.70)"
                : "#35F3DF",
          }}
        >
          {locked ? (
            <OnboardingLockIcon />
          ) : completed ? (
            <OnboardingCheckIcon />
          ) : (
            <span
              style={{
                fontSize: 23,
                lineHeight: 1,
                transform: "translateY(-1px)",
              }}
            >
              ›
            </span>
          )}
        </span>

        <span
          style={{
            minWidth: 0,
            flex: 1,
            display: "block",
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span
              style={{
                color: "#F8FAFC",
                fontSize: 17,
                lineHeight: 1.15,
                fontWeight: 850,
                letterSpacing: "-0.035em",
              }}
            >
              {scenario.title}
            </span>

            {completed ? (
              <span
                style={{
                  flex: "0 0 auto",
                  color: "#35F3DF",
                  fontSize: 10,
                  lineHeight: 1,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Пройдено
              </span>
            ) : null}
          </span>

          <span
            style={{
              display: "block",
              marginTop: 6,
              color: "rgba(203,213,225,0.70)",
              fontSize: 12.5,
              lineHeight: 1.35,
              fontWeight: 550,
            }}
          >
            {scenario.description}
          </span>

          <span
            style={{
              display: "block",
              marginTop: 7,
              color: locked
                ? "rgba(148,163,184,0.60)"
                : "rgba(165,243,252,0.60)",
              fontSize: 11,
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
{locked
  ? scenario.disabled
    ? "Пока не подключено"
    : "Сначала пройдите обзор"
  : completed
    ? "Пройти ещё раз"
    : scenario.duration}
          </span>
        </span>
      </span>
    </button>
  );
}
function OnboardingCloseButton({
  onClick,
  style,
}) {
  return (
    <button
      type="button"
      aria-label="Закрыть"
      onClick={onClick}
      style={{
        width: 40,
        height: 40,

        display: "grid",
        placeItems: "center",

        flex: "0 0 auto",

        padding: 0,

        borderRadius: 999,

        border:
          "1px solid rgba(165,243,252,0.35)",

        background:
          "rgba(53,243,223,0.10)",

        color: "#F8FAFC",

        boxShadow:
          "0 0 16px rgba(45,212,191,0.13), inset 0 1px 0 rgba(255,255,255,0.08)",

        backdropFilter: "blur(14px)",
        WebkitBackdropFilter:
          "blur(14px)",

        fontSize: 22,
        lineHeight: 1,
        fontWeight: 700,

        appearance: "none",
        WebkitAppearance: "none",

        cursor: "pointer",
        touchAction: "manipulation",

        ...style,
      }}
    >
      ×
    </button>
  );
}
function OnboardingGlobalStyles() {
  return (
    <style>{`
      html[data-fitmeup-onboarding-nav="1"]
      [data-fitmeup-bottom-nav="1"] {
        z-index: 120020 !important;

        transform:
          translate3d(-50%, 0, 0)
          !important;

        opacity: 1 !important;

        pointer-events:
          none !important;
      }

      html[data-fitmeup-onboarding-required="1"]
      [data-onboarding-cancel="1"] {
        display: none !important;
      }
    `}</style>
  );
}
function OnboardingMenu({
  open,
  progress,
  onClose,
  onStart,
}) {
  useFitmeupOnboardingNavVisible(open);

  if (!open || typeof document === "undefined") return null;

const basicScenarios = [
  ONBOARDING_SCENARIOS.overview,
  ONBOARDING_SCENARIOS.workout,
];

  return (
  <Portal>
    <OnboardingGlobalStyles />

    <div
        className="fixed inset-0 overflow-y-auto"
        style={{
          zIndex: 61000,
          background:
            "radial-gradient(circle at 0% 0%, rgba(53,243,223,0.14), transparent 34%), radial-gradient(circle at 100% 35%, rgba(14,165,233,0.09), transparent 38%), linear-gradient(180deg, #020814, #03111e 52%, #020814)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div
          className="mx-auto min-h-full"
          style={{
            width: "min(100vw, 430px)",
            padding:
  "calc(22px + env(safe-area-inset-top)) 18px calc(112px + env(safe-area-inset-bottom))",
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[31px] font-black leading-none tracking-[-0.065em] text-white">
                Онбординг
              </div>

              <div className="mt-2.5 max-w-[290px] text-[14px] font-medium leading-snug text-slate-300/70">
                Практическое знакомство с основными возможностями приложения
              </div>
            </div>

           <OnboardingCloseButton
  onClick={onClose}
/>
          </div>

          <section className="mt-8">
            <div className="mb-3 flex items-center justify-between">
              <div>
<div className="text-[13px] font-extrabold uppercase tracking-[0.14em] text-[#35F3DF]">
  Онбординги
</div>

<div className="mt-1 text-[12px] font-medium text-slate-400/70">
  Еда и тренировки
</div>
              </div>

              <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[10px] font-bold text-slate-300/65">
                2 раздела
              </span>
            </div>

            <div className="grid gap-3">
              {basicScenarios.map((scenario) => (
                <OnboardingScenarioCard
                  key={scenario.id}
                  scenario={scenario}
completed={
  progress?.completed?.[
    scenario.id
  ] === true
}
                  locked={scenario.disabled === true}
                  onClick={() => onStart(scenario.id)}
                />
              ))}
            </div>
          </section>


        </div>
      </div>
    </Portal>
  );
}
function OnboardingCompleteStep({
  step,
  onDone,
}) {
  const { t: tr } = useTranslation();

  const isOverviewComplete =
    step?.id === "overview-complete";

  const getCompleteText = (
    field,
    fallback,
    options = {}
  ) => {
    if (!isOverviewComplete) {
      return fallback;
    }

    return tr(
      `onboarding.overview.complete.${field}`,
      {
        defaultValue: fallback,
        ...options,
      }
    );
  };

  const colors = [
    "#35F3DF",
    "#20D8C3",
    "#7DD3FC",
    "#FDE68A",
    "#F9A8D4",
  ];

  const chooseReminder = (
    reminderHours
  ) => {
    onDone?.({
      reminderHours,
    });
  };

  return (
    <Portal>
      <style>{`
        @keyframes fitmeupOnboardingConfetti {
          0% {
            opacity: 0;
            transform:
              translate3d(0, -18px, 0)
              rotate(0deg);
          }

          14% {
            opacity: 1;
          }

          100% {
            opacity: 0;
            transform:
              translate3d(
                var(--confetti-x),
                170px,
                0
              )
              rotate(
                var(--confetti-rotate)
              );
          }
        }
      `}</style>

      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 119999,

          display: "grid",
          placeItems: "center",

          padding:
            "18px 18px calc(104px + env(safe-area-inset-bottom, 0px))",

          background:
            "rgba(1,7,16,0.78)",

          backdropFilter:
            "blur(12px) saturate(115%)",

          WebkitBackdropFilter:
            "blur(12px) saturate(115%)",
        }}
      >
        <div
          style={{
            position: "relative",

            width: "min(100%, 356px)",

            overflow: "hidden",

            borderRadius: 28,

            border:
              "1px solid rgba(53,243,223,0.26)",

            background:
              "radial-gradient(circle at 50% 0%, rgba(53,243,223,0.16), transparent 48%), linear-gradient(155deg, rgba(8,29,41,0.99), rgba(3,14,25,0.99))",

            boxShadow:
              "0 24px 70px rgba(0,0,0,0.50), 0 0 28px rgba(53,243,223,0.10), inset 0 1px 0 rgba(255,255,255,0.06)",

            padding:
              "28px 20px 20px",

            textAlign: "center",
          }}
        >
          {Array.from({
            length: 18,
          }).map((_, index) => (
            <span
              key={index}
              aria-hidden="true"
              style={{
                position: "absolute",

                left:
                  `${7 + ((index * 19) % 86)}%`,

                top:
                  -18 - (index % 4) * 7,

                width:
                  index % 3 === 0
                    ? 7
                    : 5,

                height:
                  index % 3 === 0
                    ? 12
                    : 9,

                borderRadius: 3,

                background:
                  colors[
                    index %
                      colors.length
                  ],

                "--confetti-x":
                  `${
                    (
                      index % 2 === 0
                        ? 1
                        : -1
                    ) *
                    (
                      18 +
                      (index % 5) * 8
                    )
                  }px`,

                "--confetti-rotate":
                  `${180 + index * 31}deg`,

                animation:
                  "fitmeupOnboardingConfetti 1150ms ease-out forwards",

                animationDelay:
                  `${(index % 6) * 55}ms`,

                pointerEvents:
                  "none",
              }}
            />
          ))}

          <div
            style={{
              position: "relative",
              zIndex: 1,
            }}
          >
            <div
              style={{
                margin: "0 auto",

                width: 54,
                height: 54,

                display: "grid",
                placeItems: "center",

                borderRadius: 999,

                background:
                  "#35F3DF",

                color: "#031018",

                boxShadow:
                  "0 0 24px rgba(53,243,223,0.32)",

                fontSize: 27,
                fontWeight: 900,
              }}
            >
              ✓
            </div>

            <div
              style={{
                marginTop: 16,

                color: "#F8FAFC",

                fontSize: 26,
                lineHeight: 1.08,
                fontWeight: 900,

                letterSpacing:
                  "-0.05em",
              }}
            >
              {getCompleteText(
                "title",
                step.title
              )}
            </div>

<div
  style={{
    marginTop: 10,

    color:
      "rgba(226,232,240,0.76)",

    fontSize: 14,
    lineHeight: 1.48,
    fontWeight: 550,

    whiteSpace: "pre-line",
  }}
>
  {getCompleteText(
    "text",
    step.text
  )}
</div>

            {step.reminderChoice ? (
              <>
                <div
                  style={{
                    marginTop: 20,

                    color:
                      "#F8FAFC",

                    fontSize: 15,
                    lineHeight: 1.3,
                    fontWeight: 800,
                  }}
                >
                  {getCompleteText(
                    "reminderTitle",
                    step.reminderTitle ||
                      "Когда вам напомнить?"
                  )}
                </div>

                <div
                  style={{
                    marginTop: 12,

                    display: "grid",

                    gridTemplateColumns:
                      "1fr 1fr",

                    gap: 9,
                  }}
                >
                  {[1, 2, 3, 4].map(
                    (hours) => (
                      <button
                        key={hours}
                        type="button"
                        onClick={() =>
                          chooseReminder(
                            hours
                          )
                        }
                        style={{
                          minHeight: 46,

                          borderRadius:
                            14,

                          border:
                            "1px solid rgba(53,243,223,0.22)",

                          background:
                            "rgba(53,243,223,0.10)",

                          color:
                            "#E6FFFB",

                          fontSize: 14,
                          fontWeight: 800,

                          appearance:
                            "none",

                          WebkitAppearance:
                            "none",
                        }}
                      >
                        {isOverviewComplete
                          ? hours === 1
                            ? getCompleteText(
                                "reminderOneHour",
                                "Через 1 час"
                              )
                            : getCompleteText(
                                "reminderHours",
                                `Через ${hours} часа`,
                                { hours }
                              )
                          : (
                            <>
                              Через {hours}{" "}
                              {hours === 1
                                ? "час"
                                : "часа"}
                            </>
                          )}
                      </button>
                    )
                  )}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    chooseReminder(null)
                  }
                  style={{
                    marginTop: 9,

                    width: "100%",
                    minHeight: 42,

                    border: 0,

                    background:
                      "transparent",

                    color:
                      "rgba(226,232,240,0.68)",

                    fontSize: 13,
                    fontWeight: 700,

                    appearance:
                      "none",

                    WebkitAppearance:
                      "none",
                  }}
                >
                  {getCompleteText(
                    "noReminder",
                    "Не напоминать"
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() =>
                  onDone?.()
                }
                style={{
                  marginTop: 18,

                  width: "100%",
                  minHeight: 48,

                  border: 0,
                  borderRadius: 16,

                  background:
                    "linear-gradient(135deg, #20D8C3, #35F3DF)",

                  color: "#031018",

                  fontSize: 15,
                  fontWeight: 900,

                  appearance: "none",

                  WebkitAppearance:
                    "none",
                }}
              >
                {step.buttonLabel ||
                  "Готово"}
              </button>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
function OnboardingOverlay({
  session,
  onNext,
  onFinish,
}) {
  const { t: tr } = useTranslation();

  const scenario = session
    ? ONBOARDING_SCENARIOS[session.scenarioId]
    : null;

  const steps = Array.isArray(scenario?.steps)
    ? scenario.steps
    : [];

  const stepIndex = Math.max(
    0,
    Number(session?.stepIndex || 0)
  );

const step = steps[stepIndex] || null;

const getOverviewStepText = (
  field
) => {
  const fallback =
    step?.[field];

  if (
    session?.scenarioId !==
      "overview" ||
    !step?.id ||
    !fallback
  ) {
    return fallback;
  }

  return tr(
    `onboarding.overview.steps.${step.id}.${field}`,
    {
      defaultValue: fallback,
    }
  );
};

const required =
  step?.kind === "action";

const isComplete =
  step?.kind === "complete";

// Финальное поздравление не считаем
// обычным шагом онбординга.
const getCountableSteps =
  (scenarioId) => {
    const scenarioSteps =
      ONBOARDING_SCENARIOS[
        scenarioId
      ]?.steps;

    return Array.isArray(
      scenarioSteps
    )
      ? scenarioSteps.filter(
          (item) =>
            item?.kind !==
            "complete"
        )
      : [];
  };

const currentScenarioSteps =
  getCountableSteps(
    session?.scenarioId
  );

const currentStepPosition =
  Math.max(
    1,
    steps
      .slice(
        0,
        stepIndex + 1
      )
      .filter(
        (item) =>
          item?.kind !==
          "complete"
      ).length
  );

const totalStepCount =
  currentScenarioSteps.length;

const currentStepNumber =
  currentStepPosition;

  const [targetBox, setTargetBox] = useState(null);
  const [secondaryBox, setSecondaryBox] = useState(null);

  const [tooltipHeight, setTooltipHeight] = useState(104);

  const tooltipRef = useRef(null);

useFitmeupOnboardingNavVisible(
  Boolean(step) &&
    step?.hideAppNav !== true
);

// В DOM хранится только факт того,
// что сейчас идёт обязательный шаг.
// Название шага основное приложение
// не получает и не использует.
useEffect(() => {
  if (
    typeof document === "undefined"
  ) {
    return undefined;
  }

  const root =
    document.documentElement;

  if (required) {
    root.dataset
      .fitmeupOnboardingRequired =
      "1";
  } else {
    delete root.dataset
      .fitmeupOnboardingRequired;
  }

  return () => {
    delete root.dataset
      .fitmeupOnboardingRequired;
  };
}, [required]);

// Продолжаем сценарий после обычного
// события настоящего приложения.
useEffect(() => {
  if (
    !step ||
    !required ||
    !step.waitFor
  ) {
    return undefined;
  }

  return subscribeAppEvents(
    ({ type }) => {
      if (
        type === step.waitFor
      ) {
        onNext();
      }
    }
  );
}, [
  step?.id,
  step?.waitFor,
  required,
  onNext,
]);

  // Находим настоящий элемент приложения.
  useEffect(() => {
    if (
  !step?.target ||
  typeof document === "undefined" ||
  typeof window === "undefined"
) {
  setTargetBox(null);
  setSecondaryBox(null);
  return undefined;
}

    let stopped = false;
    let retryTimer = 0;
    let updateTimer = 0;

    const readTarget = () => {
      if (stopped) return;

const element =
  document.querySelector(step.target);

const secondaryElement =
  step.secondaryTarget
    ? document.querySelector(
        step.secondaryTarget
      )
    : null;

const requiredElementMissing =
  !element ||
  (
    step.secondaryTarget &&
    !secondaryElement
  );

if (requiredElementMissing) {
  setTargetBox(null);
  setSecondaryBox(null);

  retryTimer = window.setTimeout(
    readTarget,
    80
  );

  return;
}

      const firstRect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const canFitInViewport =
        firstRect.height < viewportHeight - 40;

      const outsideViewport =
        firstRect.top < 12 ||
        firstRect.bottom > viewportHeight - 112;

if (
  step.centerTarget === true ||
  (
    step.autoScroll !== false &&
    canFitInViewport &&
    outsideViewport
  )
) {
  try {
    element.scrollIntoView({
      block: "center",
      inline: "nearest",
      behavior: "auto",
    });
  } catch {}
}

      const updateBox = () => {
        if (
  stopped ||
  !element.isConnected ||
  (
    secondaryElement &&
    !secondaryElement.isConnected
  )
) {
  return;
}

const padding = Number(step.padding ?? 6);

const viewportWidth = window.innerWidth;
const viewportHeightNow = window.innerHeight;

const makeTargetBox = (
  currentElement,
  forceCircle = false
) => {
  const rect =
    currentElement.getBoundingClientRect();

  const styles =
    window.getComputedStyle(currentElement);

  const left = Math.max(
    6,
    rect.left - padding
  );

  const top = Math.max(
    6,
    rect.top - padding
  );

  const right = Math.min(
    viewportWidth - 6,
    rect.right + padding
  );

  const bottom = Math.min(
    viewportHeightNow - 6,
    rect.bottom + padding
  );

  const width = Math.max(
    1,
    right - left
  );

  const height = Math.max(
    1,
    bottom - top
  );

  const maximumRadius =
    Math.min(width, height) / 2;

  const elementRadius =
    Number.parseFloat(
      styles.borderTopLeftRadius
    ) || 18;

  const radius = forceCircle
    ? maximumRadius
    : Math.min(
        elementRadius + padding,
        maximumRadius
      );

  return {
    left,
    top,
    right,
    bottom,
    width,
    height,
    radius,
  };
};

setTargetBox(
  makeTargetBox(
    element,
    step.shape === "circle"
  )
);

setSecondaryBox(
  secondaryElement
    ? makeTargetBox(secondaryElement)
    : null
);
      };

      requestAnimationFrame(updateBox);
      updateTimer = window.setTimeout(updateBox, 180);
    };

    readTarget();

    const updateOnMove = () => {
      window.clearTimeout(updateTimer);
      updateTimer = window.setTimeout(readTarget, 16);
    };

    window.addEventListener("resize", updateOnMove);
    document.addEventListener(
      "scroll",
      updateOnMove,
      true
    );

    return () => {
      stopped = true;
      window.clearTimeout(retryTimer);
      window.clearTimeout(updateTimer);

      window.removeEventListener(
        "resize",
        updateOnMove
      );

      document.removeEventListener(
        "scroll",
        updateOnMove,
        true
      );
    };
  }, [
  step?.id,
  step?.target,
  step?.secondaryTarget,
  step?.padding,
  step?.shape,
  step?.autoScroll,
  step?.centerTarget,
]);

  useLayoutEffect(() => {
    if (!tooltipRef.current) return;

    const rect =
      tooltipRef.current.getBoundingClientRect();

    if (rect.height > 0) {
      setTooltipHeight(rect.height);
    }
 }, [
  step?.id,
  step?.hideTooltip,
  targetBox?.left,
    targetBox?.top,
    targetBox?.width,
  ]);

  if (
    !step ||
    typeof document === "undefined" ||
    typeof window === "undefined"
  ) {
    return null;
  }
if (isComplete) {
  return (
    <OnboardingCompleteStep
      step={step}
      onDone={onNext}
    />
  );
}
  // При смене страницы ждём появления цели,
  // а не показываем очередную карточку в пустом месте.
  if (
  (step.target && !targetBox) ||
  (
    step.secondaryTarget &&
    !secondaryBox
  )
) {
  return null;
}

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const tooltipWidth = Math.min(
    326,
    viewportWidth - 24
  );

  let tooltipLeft =
    targetBox
      ? targetBox.left +
        targetBox.width / 2 -
        tooltipWidth / 2
      : (viewportWidth - tooltipWidth) / 2;

  tooltipLeft = Math.max(
    12,
    Math.min(
      tooltipLeft,
      viewportWidth - tooltipWidth - 12
    )
  );

  let tooltipTop = 12;

  if (step.placement !== "top-fixed" && targetBox) {
    let showAbove = step.placement === "above";

    if (step.placement !== "above" && step.placement !== "below") {
      const spaceAbove = targetBox.top;
      const spaceBelow =
        viewportHeight - targetBox.bottom;

      showAbove = spaceAbove > spaceBelow;
    }

    if (
      !step.forcePlacement &&
      showAbove &&
      targetBox.top - tooltipHeight - 14 < 8
    ) {
      showAbove = false;
    }

    if (
      !step.forcePlacement &&
      !showAbove &&
      targetBox.bottom + tooltipHeight + 14 >
        viewportHeight - 132
    ) {
      showAbove = true;
    }

    tooltipTop = showAbove
      ? targetBox.top - tooltipHeight - 14
      : targetBox.bottom + 14;

    const tooltipBottomReserve =
      step.forcePlacement ? 12 : 132;

    tooltipTop = Math.max(
      8,
      Math.min(
        tooltipTop,
        viewportHeight -
          tooltipHeight -
          tooltipBottomReserve
      )
    );
  }

  const maskStyle = {
  position: "fixed",
  zIndex: 120000,

  // Эти четыре области теперь только блокируют нажатия.
  // Само затемнение рисуется SVG-маской ниже.
  background: "transparent",

  pointerEvents: "auto",
};
const stopInfoTarget = (event) => {
  event.preventDefault();
  event.stopPropagation();
};
  const onboardingMaskId =
  `fitmeup-onboarding-mask-${String(
    step.id || "step"
  ).replace(/[^a-zA-Z0-9_-]/g, "-")}`;

  return (
  <Portal>
    <OnboardingGlobalStyles />

    <style>{`
        
      `}</style>
{targetBox ? (
  <svg
    aria-hidden="true"
    width={viewportWidth}
    height={viewportHeight}
    viewBox={`0 0 ${viewportWidth} ${viewportHeight}`}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 119999,
      width: "100vw",
      height: "100vh",
      pointerEvents: "none",
    }}
  >
    <defs>
      <mask
        id={onboardingMaskId}
        maskUnits="userSpaceOnUse"
      >
        <rect
          x="0"
          y="0"
          width={viewportWidth}
          height={viewportHeight}
          fill="white"
        />

        <rect
          x={targetBox.left}
          y={targetBox.top}
          width={targetBox.width}
          height={targetBox.height}
          rx={targetBox.radius}
          ry={targetBox.radius}
          fill="black"
        />

        {secondaryBox ? (
<rect
  x={targetBox.left}
  y={targetBox.top}
  width={targetBox.width}
  height={targetBox.height}
  rx={targetBox.radius}
  ry={targetBox.radius}
  fill="white"
  fillOpacity={
    step.dimTarget ? 0.18 : 0
  }
/>
        ) : null}
      </mask>
    </defs>

    <rect
      x="0"
      y="0"
      width={viewportWidth}
      height={viewportHeight}
      fill={`rgba(1, 7, 16, ${Number(
  step.overlayOpacity ?? 0.80
)})`}
      mask={`url(#${onboardingMaskId})`}
    />
  </svg>
) : null}
      {targetBox ? (
        <>
          {/* Затемнение сверху */}
          <div
            style={{
              ...maskStyle,
              left: 0,
              top: 0,
              right: 0,
              height: Math.max(0, targetBox.top),
            }}
          />

          {/* Затемнение снизу */}
          <div
            style={{
              ...maskStyle,
              left: 0,
              top: targetBox.bottom,
              right: 0,
              bottom: 0,
            }}
          />

          {/* Затемнение слева */}
          <div
            style={{
              ...maskStyle,
              left: 0,
              top: targetBox.top,
              width: Math.max(0, targetBox.left),
              height: targetBox.height,
            }}
          />

          {/* Затемнение справа */}
          <div
            style={{
              ...maskStyle,
              left: targetBox.right,
              top: targetBox.top,
              right: 0,
              height: targetBox.height,
            }}
          />

          {/* Точная подсветка элемента */}
          <div
            style={{
              position: "fixed",
              zIndex: 120001,
              left: targetBox.left,
              top: targetBox.top,
              width: targetBox.width,
              height: targetBox.height,
              borderRadius: targetBox.radius,
              border: "2px solid #35F3DF",
              boxShadow:
                "0 0 0 4px rgba(53,243,223,0.12), 0 0 22px rgba(53,243,223,0.40)",
              pointerEvents: "none",
            }}
          />
{secondaryBox ? (
  <div
    style={{
      position: "fixed",
      zIndex: 120001,
      left: secondaryBox.left,
      top: secondaryBox.top,
      width: secondaryBox.width,
      height: secondaryBox.height,
      borderRadius: secondaryBox.radius,
      border: "2px solid #35F3DF",
      boxShadow:
        "0 0 0 4px rgba(53,243,223,0.12), 0 0 22px rgba(53,243,223,0.40)",
      pointerEvents: "none",
    }}
  />
) : null}
          {/* На информационном шаге цель только показываем,
              но не нажимаем. */}
          {step.kind === "info" ? (
            <div
              onPointerDown={stopInfoTarget}
              onClick={stopInfoTarget}
              style={{
                position: "fixed",
                zIndex: 120002,
                left: targetBox.left,
                top: targetBox.top,
                width: targetBox.width,
                height: targetBox.height,
                borderRadius: targetBox.radius,
                background: "transparent",
                pointerEvents: "auto",
              }}
            />
          ) : null}
        </>
      ) : null}

 {!step.hideTooltip ? (
  <div
    ref={tooltipRef}
    style={{
      position: "fixed",
      zIndex: 120010,

      left: tooltipLeft,

top:
  step.placement === "top-fixed"
    ? "calc(12px + env(safe-area-inset-top, 0px))"
    : step.placement === "bottom-fixed"
      ? "auto"
      : tooltipTop,

bottom:
  step.placement === "bottom-fixed"
    ? "calc(18px + env(safe-area-inset-bottom, 0px))"
    : "auto",

width: tooltipWidth,

      boxSizing: "border-box",

      padding: "14px 15px",

      borderRadius: 20,

      border:
        "1px solid rgba(255,255,255,0.10)",

      background:
        "linear-gradient(155deg, rgba(8,29,41,0.98), rgba(3,14,25,0.99))",

      boxShadow:
        "0 18px 50px rgba(0,0,0,0.46), inset 0 1px 0 rgba(255,255,255,0.055)",

      color: "#F8FAFC",

      pointerEvents: "auto",
    }}
  >
    <div
      style={{
        color: "#F8FAFC",

        fontSize: 18,
        lineHeight: 1.12,
        fontWeight: 850,

        letterSpacing:
          "-0.035em",
      }}
    >
      {getOverviewStepText("title")}
    </div>

    {getOverviewStepText("text") ? (
      <div
        style={{
          marginTop: 6,

          color:
            "rgba(226,232,240,0.76)",

          WebkitTextFillColor:
            "rgba(226,232,240,0.76)",

fontSize: 13,
lineHeight: 1.42,
fontWeight: 550,
whiteSpace: "pre-line",
        }}
      >
        {getOverviewStepText("text")}
      </div>
    ) : null}

    {step.note ? (
      <div
        style={{
          marginTop: 9,

          color:
            "rgba(165,243,252,0.70)",

          WebkitTextFillColor:
            "rgba(165,243,252,0.70)",

          fontSize: 12,
          lineHeight: 1.42,
          fontWeight: 650,
        }}
      >
        {step.note}
      </div>
    ) : null}

    {required ? (
      <div
        style={{
          marginTop: 11,

          display: "flex",
          alignItems: "center",

          gap: 7,

          color:
            "rgba(165,243,252,0.72)",

          fontSize: 11.5,
          lineHeight: 1.2,
          fontWeight: 700,
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,

            flex: "0 0 auto",

            borderRadius: 999,

            background: "#35F3DF",

            boxShadow:
              "0 0 10px rgba(53,243,223,0.70)",
          }}
        />

{getOverviewStepText("actionHint") ||
  "Выполните действие в подсвеченной области"}
      </div>
    ) : (
      <button
        type="button"
        onClick={onNext}
        style={{
          marginTop: 12,

          minHeight: 42,
          width: "100%",

          border: 0,
          borderRadius: 14,

          background:
            "linear-gradient(135deg, #20D8C3, #35F3DF)",

          color: "#031018",

          fontSize: 14,
          fontWeight: 850,

          appearance: "none",
          WebkitAppearance:
            "none",
        }}
      >
                {session?.scenarioId === "overview"
          ? tr("onboarding.controls.next")
          : "Далее"}
      </button>
    )}
  </div>
) : null}

{!step.hideFinish ? (
  <>
    {/* Слева — старая кнопка завершения */}
<button
  type="button"
  data-onboarding-footer="1"
  onClick={() =>
    onFinish?.()
  }
      style={{
        position: "fixed",
        zIndex: 120030,

        left:
          "max(22px, calc(50% - 177px))",

        bottom:
          "calc(84px + env(safe-area-inset-bottom, 0px))",

        minHeight: 38,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        padding: "0 13px",

        boxSizing: "border-box",

        borderRadius: 14,

        border:
          "1px solid rgba(255,255,255,0.11)",

        background:
          "rgba(4,16,28,0.92)",

        color:
          "rgba(226,232,240,0.76)",

        boxShadow:
          "0 10px 28px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.045)",

        backdropFilter:
          "blur(14px)",

        WebkitBackdropFilter:
          "blur(14px)",

        fontSize: 11.5,
        lineHeight: 1,
        fontWeight: 700,

        appearance: "none",

        WebkitAppearance:
          "none",

        whiteSpace: "nowrap",
      }}
    >
            {session?.scenarioId === "overview"
        ? tr("onboarding.controls.finish")
        : "Завершить онбординг"}
    </button>

    {/* Справа — номер текущего шага */}
    <div
      style={{
        position: "fixed",
        zIndex: 120030,

        right:
          "max(22px, calc(50% - 177px))",

        bottom:
          "calc(84px + env(safe-area-inset-bottom, 0px))",

        minHeight: 38,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        padding: "0 13px",

        boxSizing: "border-box",

        borderRadius: 14,

        border:
          "1px solid rgba(255,255,255,0.11)",

        background:
          "rgba(4,16,28,0.92)",

        color:
          "rgba(226,232,240,0.76)",

        boxShadow:
          "0 10px 28px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.045)",

        backdropFilter:
          "blur(14px)",

        WebkitBackdropFilter:
          "blur(14px)",

        fontSize: 11.5,
        lineHeight: 1,
        fontWeight: 700,

        whiteSpace: "nowrap",

        pointerEvents: "none",
      }}
    >
      {session?.scenarioId === "overview"
        ? tr(
            "onboarding.controls.stepCounter",
            {
              current: Math.min(
                currentStepNumber,
                totalStepCount
              ),
              total: totalStepCount,
            }
          )
        : (
          <>
            Шаг{" "}
            {Math.min(
              currentStepNumber,
              totalStepCount
            )}{" "}
            из{" "}
            {totalStepCount}
          </>
        )}
    </div>
  </>
) : null}
    </Portal>
  );
}
function clearOnboardingDomState() {
  if (typeof document === "undefined") {
    return;
  }

  const root =
    document.documentElement;

  delete root.dataset
    .fitmeupOnboardingRequired;

  delete root.dataset
    .fitmeupOnboardingStep;

  delete root.dataset
    .fitmeupOnboardingNavCount;

  delete root.dataset
    .fitmeupOnboardingNav;
}

function useOnboardingController({
  prepareStep,
  beforeStart,
  afterClose,
  afterComplete,
  onLocked,
} = {}) {
  const [
    menuOpen,
    setMenuOpen,
  ] = useState(false);

  const [
    session,
    setSession,
  ] = useState(null);

  const [
    progress,
    setProgress,
  ] = useState(() =>
    readOnboardingProgress()
  );

  const scenario = session
    ? ONBOARDING_SCENARIOS[
        session.scenarioId
      ]
    : null;

  const currentStep =
    scenario?.steps?.[
      Number(
        session?.stepIndex || 0
      )
    ] || null;

  const openMenu =
    useCallback(() => {
      setSession(null);
      setMenuOpen(true);
    }, []);

  const close =
    useCallback(() => {
      const scenarioId =
        session?.scenarioId || null;

      setSession(null);
      setMenuOpen(false);

      clearOnboardingDomState();

      afterClose?.(
        scenarioId
      );
    }, [
      session?.scenarioId,
      afterClose,
    ]);

  const goToStep =
    useCallback(
      (stepIndex) => {
        if (!session) return;

        const nextScenario =
          ONBOARDING_SCENARIOS[
            session.scenarioId
          ];

        if (!nextScenario) return;

        const safeIndex = Math.max(
          0,
          Math.min(
            Number(
              stepIndex || 0
            ),
            Math.max(
              0,
              nextScenario.steps.length -
                1
            )
          )
        );

        const step =
          nextScenario.steps?.[
            safeIndex
          ];

        if (!step) return;

        prepareStep?.(
          step,
          {
            scenarioId:
              session.scenarioId,

            reason: "jump",
          }
        );

        setSession(
          (current) => ({
            ...current,
            stepIndex: safeIndex,
          })
        );
      },
      [
        session,
        prepareStep,
      ]
    );

const back =
  useCallback(() => {
    if (!session) return;

    const currentStepIndex =
      Number(
        session.stepIndex || 0
      );

    // С первого шага второй части
    // возвращаемся на последний шаг
    // первой части онбординга «Еда».
    if (
      session.scenarioId ===
        "dish" &&
      currentStepIndex === 0
    ) {
      const overviewScenario =
        ONBOARDING_SCENARIOS
          .overview;

      const previousStepIndex =
        Math.max(
          0,
          overviewScenario
            .steps.length - 1
        );

      const previousStep =
        overviewScenario
          .steps[
            previousStepIndex
          ];

      if (!previousStep) return;

      prepareStep?.(
        previousStep,
        {
          scenarioId:
            "overview",

          reason:
            "back-across-scenario",
        }
      );

      setSession({
        scenarioId: "overview",
        stepIndex:
          previousStepIndex,
      });

      return;
    }

    goToStep(
      Math.max(
        0,
        currentStepIndex - 1
      )
    );
  }, [
    session,
    goToStep,
    prepareStep,
  ]);

  const start =
    useCallback(
      (scenarioId) => {
        const nextScenario =
          ONBOARDING_SCENARIOS[
            scenarioId
          ];

        if (
          !nextScenario ||
          nextScenario.disabled
        ) {
          return;
        }

        if (
          nextScenario.requires &&
          progress?.completed?.[
            nextScenario.requires
          ] !== true
        ) {
          onLocked?.(
            nextScenario
          );

          return;
        }

        const firstStep =
          nextScenario.steps?.[0];

        if (!firstStep) return;

        beforeStart?.(
          scenarioId
        );

        setMenuOpen(false);

        prepareStep?.(
          firstStep,
          {
            scenarioId,
            reason: "start",
          }
        );

        setSession({
          scenarioId,
          stepIndex: 0,
        });
      },
      [
        progress,
        beforeStart,
        prepareStep,
        onLocked,
      ]
    );

const next =
  useCallback((completion = {}) => {
    if (!session) return;

    const nextScenario =
      ONBOARDING_SCENARIOS[
        session.scenarioId
      ];

    if (!nextScenario) {
      setSession(null);
      return;
    }

    const nextStepIndex =
      Number(
        session.stepIndex || 0
      ) + 1;

    if (
      nextStepIndex <
      nextScenario.steps.length
    ) {
      const nextStep =
        nextScenario.steps[
          nextStepIndex
        ];

      prepareStep?.(
        nextStep,
        {
          scenarioId:
            session.scenarioId,

          reason: "next",
        }
      );

      setSession(
        (current) => ({
          ...current,

          stepIndex:
            nextStepIndex,
        })
      );

      return;
    }

    // Когда заканчивается первая
    // часть онбординга «Еда»,
    // сразу запускаем часть
    // «Мои продукты и блюда».
if (
  AUTO_CONTINUE_FOOD_TO_DISH &&
  nextScenario.id ===
    "overview"
) {
      const dishScenario =
        ONBOARDING_SCENARIOS.dish;

      const firstDishStep =
        dishScenario?.steps?.[0];

      if (firstDishStep) {
        prepareStep?.(
          firstDishStep,
          {
            scenarioId: "dish",
            reason: "continue",
          }
        );

        setSession({
          scenarioId: "dish",
          stepIndex: 0,
        });

        return;
      }
    }

    setProgress(
      (currentProgress) => {
        const completed =
          nextScenario.id === "dish"
            ? {
                ...currentProgress
                  .completed,

                // Весь объединённый
                // онбординг «Еда»
                // завершён.
                overview: true,
                dish: true,
              }
            : {
                ...currentProgress
                  .completed,

                [nextScenario.id]:
                  true,
              };

        const nextProgress = {
          ...currentProgress,
          completed,
        };

        writeOnboardingProgress(
          nextProgress
        );

        return nextProgress;
      }
    );

    clearOnboardingDomState();

afterComplete?.(
  nextScenario.id,
  completion
);

setSession(null);
setMenuOpen(false);
  }, [
    session,
    prepareStep,
    afterComplete,
  ]);

  return {
    menuOpen,
    session,
    progress,
    currentStep,

    openMenu,
    close,
    start,
    next,
    back,
    goToStep,
  };
}
export {
  ONBOARDING_SCENARIOS,
  OnboardingMenu,
  OnboardingOverlay,
  emitOnboardingAction,
  getEmptyOnboardingProgress,
  readOnboardingProgress,
  writeOnboardingProgress,
  useOnboardingController,
};